import * as THREE from 'three';
import { SparkRenderer } from '@sparkjsdev/spark';
import { CAMERA, RENDER } from '../config.js';

/**
 * WebGLレンダラ・シーン・カメラ・Sparkレンダラをまとめて用意する。
 * 返り値は破棄用の dispose を持つ。
 */
export function createStage(container) {
  if (!(container instanceof HTMLElement)) {
    throw new Error('createStage: 描画先の要素が不正です');
  }

  // Spark は WebGL の MSAA と相性が悪く、有効にすると速度だけ落ちるので antialias: false
  const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false });
  renderer.setClearColor(RENDER.background, 1);
  /*
   * 影。CG の物件だけで使う（buildHouse が影を落とす設定にする）。
   * スキャンの物件は面が何十万もあるため影の計算が重すぎて使えない。
   */
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(CAMERA.fov, 1, CAMERA.near, CAMERA.far);
  scene.add(camera);

  const spark = new SparkRenderer({ renderer });
  scene.add(spark);

  /**
   * 採寸マーカーなどの重ね描き用シーン。
   * スプラットと同じシーンに入れると renderOrder を上げても描画順が安定せず
   * 埋もれてしまうため、深度をクリアしてから別パスで描く。
   */
  const overlayScene = new THREE.Scene();
  renderer.autoClear = false;

  const render = () => {
    renderer.clear();
    renderer.render(scene, camera);
    renderer.clearDepth();
    renderer.render(overlayScene, camera);
  };

  const applySize = () => {
    const { clientWidth: w, clientHeight: h } = container;
    if (w === 0 || h === 0) return;
    const cap = isCoarsePointer() ? RENDER.maxPixelRatioMobile : RENDER.maxPixelRatio;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, cap));
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  applySize();

  const observer = new ResizeObserver(applySize);
  observer.observe(container);

  return {
    renderer,
    scene,
    overlayScene,
    camera,
    spark,
    render,
    dispose() {
      observer.disconnect();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}

function isCoarsePointer() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}
